import ResourceSvc from '@/services/resource.svc.js';
import { clubBackRouteMixin } from '../mixins/club-back-route.js';

import ClubDetailsSection from '../register-club/sections/club-details-section/club-details-section.vue';
import OfficersSection from '../register-club/sections/officers-section/officers-section.vue';
import JuniorMembersSection from '../register-club/sections/junior-members-section/junior-members-section.vue';
import BankDetailsSection from '../register-club/sections/bank-details-section/bank-details-section.vue';
import MembershipTypesSection from '../register-club/sections/membership-types-section/membership-types-section.vue';
import DocumentsSettingsSection from '../register-club/sections/documents-settings-section/documents-settings-section.vue';

import {
    FORM_SECTIONS,
    IRISH_COUNTIES,
    createEmptyOfficer,
    createInitialClubState,
    createInitialValidationState,
    normalizeOfficer,
    normalizeAdditionalOfficer,
    generateYearOptions,
    formatCountyOptions,
    parseIntOrNull,
    toStringOrEmpty,
    isClubRegistrationFormValid,
    hasClubRegistrationValidationErrors
} from '../register-club/constants/club-registration-constants.js';

const resourceService = new ResourceSvc();

export default {
    name: 'viewClub',

    mixins: [clubBackRouteMixin],

    components: {
        'club-details-section': ClubDetailsSection,
        'junior-members-section': JuniorMembersSection,
        'officers-section': OfficersSection,
        'bank-details-section': BankDetailsSection,
        'membership-types-section': MembershipTypesSection,
        'documents-settings-section': DocumentsSettingsSection
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            isRenewalOptInLocked: false,
            errorMessage: null,
            activeSection: 'details',

            initData: {
                yearOptions: [],
                countyOptions: [],
                availableMembershipTypes: []
            },

            assignedMembershipTypes: [],
            selectedMembershipTypeId: null,
            selectedMembershipTypePrice: null,
            newMembershipTypeName: '',

            club: {
                ...createInitialClubState(),
                status: null,
                statusString: null
            },

            validation: createInitialValidationState()
        };
    },

    computed: {
        isReadOnly() {
            return this.club.statusString === 'Active';
        },

        isRenewalOptInVisible() {
            return this.club.isRenewalOptInOffered === true;
        },

        sections() {
            return FORM_SECTIONS.filter(s => s.id !== 'submit');
        },

        currentSectionIndex() {
            return this.sections.findIndex(s => s.id === this.activeSection);
        },

        isFirstSection() {
            return this.currentSectionIndex === 0;
        },

        isLastSection() {
            return this.currentSectionIndex === this.sections.length - 1;
        },

        nextSectionId() {
            const nextIndex = this.currentSectionIndex + 1;
            return nextIndex < this.sections.length ? this.sections[nextIndex].id : null;
        },

        previousSectionId() {
            const prevIndex = this.currentSectionIndex - 1;
            return prevIndex >= 0 ? this.sections[prevIndex].id : null;
        },

        pageTitle() {
            return this.isReadOnly ? 'View Club Details' : 'Edit Club Details';
        },

        statusBadgeClass() {
            switch (this.club.statusString) {
                case 'Active':   return 'badge badge-success';
                case 'Not Paid': return 'badge badge-warning';
                case 'Canceled': return 'badge badge-danger';
                default:         return 'badge badge-secondary';
            }
        },

        isFormValid() {
            return isClubRegistrationFormValid(this.club, this.assignedMembershipTypes, this.validation);
        }
    },

    methods: {
        hasValidationErrors(sectionId) {
            if (this.isReadOnly) return false;
            return hasClubRegistrationValidationErrors(sectionId, this.club, this.assignedMembershipTypes, this.validation);
        },

        async loadData() {
            const clubId = this.$route.params.id;
            if (!clubId) {
                this.errorMessage = 'Invalid club ID';
                this.isLoading = false;
                return;
            }

            try {
                this.isLoading = true;
                this.initData.yearOptions = generateYearOptions();
                this.initData.countyOptions = formatCountyOptions(IRISH_COUNTIES);
                await this.loadRenewalOptInLockState();
                await Promise.all([
                    this.loadClubDetails(clubId),
                    this.loadMembershipTypes()
                ]);
            } catch (error) {
                this.errorMessage = error.message || 'Failed to load data';
            } finally {
                this.isLoading = false;
            }
        },

        async loadClubDetails(clubId) {
            const year = parseInt(this.$route.query.year, 10) || null;
            const response = await resourceService.getClubDetailsForCopy(clubId, year);
            if (response && response.body && response.body.isSuccess) {
                this.applyClubData(response.body.outputData);
            } else {
                this.errorMessage = (response && response.body && response.body.message) ||
                    'Failed to load club details';
            }
        },

        async loadRenewalOptInLockState() {
            try {
                const response = await resourceService.getClubRenewalOptInLockState();
                if (response && response.body && response.body.isSuccess) {
                    this.isRenewalOptInLocked = !!(response.body.outputData && response.body.outputData.isLocked);
                }
            } catch (error) {
                console.error('Failed to load club renewal opt-in lock state:', error);
            }
        },

        async loadMembershipTypes() {
            try {
                const response = await resourceService.getAllMembershipTypes();
                if (response && response.body && response.body.isSuccess) {
                    const types = response.body.outputData || [];
                    this.initData.availableMembershipTypes = types.map(t => ({
                        label: t.clubMembershipTypeLabel || t.clubMembershipType,
                        value: t.clubMembershipTypeId
                    }));
                }
            } catch (error) {
                console.error('Failed to load membership types:', error);
            }
        },

        applyClubData(sourceClub) {
            Object.assign(this.club, {
                id: sourceClub.id,
                name: sourceClub.name,
                clubEmail: sourceClub.clubEmail,
                clubWebsite: sourceClub.clubWebsite,
                town: sourceClub.town,
                county: sourceClub.county,
                yearFormed: parseIntOrNull(sourceClub.yearFormed),
                noOfMembersLastYear: parseIntOrNull(sourceClub.noOfMembersLastYear),
                membersLimit: sourceClub.membersLimit,
                facebook: sourceClub.facebook,
                twitter: sourceClub.twitter,
                year: sourceClub.year || new Date().getFullYear(),
                status: sourceClub.status,
                statusString: sourceClub.statusString
            });

            this.club.chairperson = normalizeOfficer(sourceClub.chairperson);
            this.club.treasurer = normalizeOfficer(sourceClub.treasurer);
            this.club.secretary = normalizeOfficer(sourceClub.secretary);
            this.club.membershipOfficer = normalizeOfficer(sourceClub.membershipOfficer);
            this.club.womensOfficer = normalizeOfficer(sourceClub.womensOfficer);
            this.club.safeguardingOfficer = normalizeOfficer(sourceClub.safeguardingOfficer);
            this.club.inclusionDiversityOfficer = normalizeOfficer(sourceClub.inclusionDiversityOfficer);
            this.club.designatedLiaisonPerson = normalizeOfficer(sourceClub.designatedLiaisonPerson);
            this.club.additionalOfficers = (sourceClub.additionalOfficers || []).map(normalizeAdditionalOfficer);

            this.club.hasJuniorMembers = sourceClub.hasJuniorMembers === 'Yes';
            this.club.allowClubRenewalOptIn = this.isRenewalOptInLocked && sourceClub.statusString !== 'Active'
                ? 'No'
                : (sourceClub.allowClubRenewalOptIn || null);
            this.club.isRenewalOptInOffered = sourceClub.isRenewalOptInOffered === true;
            this.club.juniorMembersLimit = parseIntOrNull(sourceClub.juniorMembersLimit);
            this.club.juniorAgeGroups = [...(sourceClub.juniorAgeGroups || [])];
            this.club.juniorAgeGroupsAdditional = sourceClub.juniorAgeGroupsAdditional;

            Object.assign(this.club, {
                bankAccountName: sourceClub.bankAccountName,
                bankName: sourceClub.bankName,
                bankAddress: sourceClub.bankAddress,
                iban: sourceClub.iban,
                bicNumber: sourceClub.bicNumber
            });

            this.club.safeguardStatementLink = sourceClub.safeguardStatementLink;
            this.club.safeguardStatementDescription = sourceClub.safeguardStatementDescription;
            this.club.notificationState = sourceClub.notificationState;
            this.club.notificationEmailAddress = sourceClub.notificationEmailAddress;
            this.club.feePaymentType = sourceClub.feePaymentType || 'Online';
            this.club.acceptTerms = sourceClub.acceptTerms;

            this.club.termsConditionsFiles = (sourceClub.termsConditionsFiles || []).map(f => ({
                name: f.name,
                id: f.id,
                uid: `server_file_${f.id}`
            }));
            this.club.safeguardingStatementFiles = (sourceClub.safeguardingStatementFiles || []).map(f => ({
                name: f.name,
                id: f.id,
                uid: `server_file_${f.id}`
            }));

            this.assignedMembershipTypes = (sourceClub.clubMembershipTypes || []).map(t => ({
                id: t.id || 0,
                clubMembershipTypeId: t.clubMembershipTypeId,
                name: t.clubMembershipTypeLabel || t.clubMembershipType,
                price: t.price
            }));
        },

        getPayload() {
            const { club } = this;
            return {
                id: club.id,
                name: club.name,
                membersLimit: club.membersLimit,
                year: club.year || new Date().getFullYear(),
                clubWebsite: club.clubWebsite,
                clubEmail: club.clubEmail,
                town: club.town,
                county: club.county,
                noOfMembersLastYear: toStringOrEmpty(club.noOfMembersLastYear),
                yearFormed: toStringOrEmpty(club.yearFormed),
                facebook: club.facebook || '',
                twitter: club.twitter || '',
                hasJuniorMembers: club.hasJuniorMembers === true ? 'Yes' : 'No',
                allowClubRenewalOptIn: this.isRenewalOptInLocked
                    ? 'No'
                    : (club.allowClubRenewalOptIn || null),
                juniorMembersLimit: toStringOrEmpty(club.juniorMembersLimit),
                juniorAgeGroups: club.juniorAgeGroups || [],
                juniorAgeGroupsAdditional: club.juniorAgeGroupsAdditional || '',
                chairperson: club.chairperson || createEmptyOfficer(),
                treasurer: club.treasurer || createEmptyOfficer(),
                secretary: club.secretary || createEmptyOfficer(),
                membershipOfficer: club.membershipOfficer || createEmptyOfficer(),
                womensOfficer: club.womensOfficer || createEmptyOfficer(),
                inclusionDiversityOfficer: club.inclusionDiversityOfficer || createEmptyOfficer(),
                safeguardingOfficer: club.safeguardingOfficer || createEmptyOfficer(),
                designatedLiaisonPerson: club.designatedLiaisonPerson || createEmptyOfficer(),
                additionalOfficers: club.additionalOfficers || [],
                bankAccountName: club.bankAccountName || '',
                bankName: club.bankName || '',
                bankAddress: club.bankAddress || '',
                iban: club.iban || '',
                bicNumber: club.bicNumber || '',
                safeguardStatementLink: club.safeguardStatementLink || '',
                safeguardStatementDescription: club.safeguardStatementDescription || '',
                feePaymentType: club.feePaymentType || 'Online',
                acceptTerms: club.acceptTerms === true,
                notificationState: club.notificationState === true,
                notificationEmailAddress: club.notificationEmailAddress || '',
                clubMembershipTypes: this.assignedMembershipTypes.map(t => ({
                    id: t.id || 0,
                    clubMembershipTypeId: t.clubMembershipTypeId,
                    clubMembershipTypeLabel: t.name || '',
                    clubMembershipType: t.name || '',
                    price: Number(t.price) || 0
                })),
                termsConditionsFiles: club.termsConditionsFiles || [],
                safeguardingStatementFiles: club.safeguardingStatementFiles || [],
                userMembersCount: 0
            };
        },

        getClubFile(fileType) {
            return resourceService.getClubFile(this.club.id, fileType, this.club.year);
        },

        async saveClub() {
            if (!this.isFormValid) {
                this.$showError('Please fill in all required fields and correct invalid values');
                return;
            }

            try {
                this.isSaving = true;
                const response = await resourceService.updateClubRegistration(this.getPayload());
                if (response && response.body && response.body.isSuccess) {
                    this.$showSuccess('Club details saved successfully');
                    this.isSaving = false;
                    this.$router.push('/dashboard/club/management');
                } else {
                    const errorMsg = (response && response.body && response.body.message) ||
                        'Failed to save club';
                    this.$showError(errorMsg);
                    this.isSaving = false;
                }
            } catch (error) {
                this.$showError(error.message || 'An error occurred');
                this.isSaving = false;
            }
        },

        assignMembershipType() {
            if (!this.selectedMembershipTypeId) {
                this.$showError('Please select a membership type');
                return;
            }
            if (this.selectedMembershipTypePrice === null || this.selectedMembershipTypePrice < 0) {
                this.$showError('Please enter a valid price');
                return;
            }
            const selectedType = this.initData.availableMembershipTypes.find(
                t => t.value === this.selectedMembershipTypeId
            );
            if (!selectedType) return;

            const tempId = -(this.assignedMembershipTypes.length + 1);
            this.assignedMembershipTypes.push({
                id: tempId,
                clubMembershipTypeId: selectedType.value,
                name: selectedType.label,
                price: this.selectedMembershipTypePrice
            });

            this.selectedMembershipTypeId = null;
            this.selectedMembershipTypePrice = null;
        },

        removeMembershipType(index) {
            this.assignedMembershipTypes.splice(index, 1);
        },

        addNewMembershipType() {
            const trimmedName = this.newMembershipTypeName ? this.newMembershipTypeName.trim() : '';
            if (!trimmedName) {
                this.$showError('Please enter a membership type name');
                return;
            }
            const nameExists = this.initData.availableMembershipTypes.some(
                t => t.label.toLowerCase() === trimmedName.toLowerCase()
            );
            if (nameExists) {
                this.$showError('A membership type with this name already exists');
                return;
            }
            const tempId = -(this.initData.availableMembershipTypes.length + 1000);
            this.initData.availableMembershipTypes.push({ label: trimmedName, value: tempId });
            this.selectedMembershipTypeId = tempId;
            this.newMembershipTypeName = '';
            this.$showSuccess('New membership type created. Set the price and click Assign.');
        },

        goToNextSection() {
            if (this.nextSectionId) {
                this.navigateToSection(this.nextSectionId);
            }
        },

        goToPreviousSection() {
            if (this.previousSectionId) {
                this.navigateToSection(this.previousSectionId);
            }
        },

        navigateToSection(sectionId) {
            this.activeSection = sectionId;
            this.$nextTick(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        },

        handlePageShow(event) {
            if (event.persisted) {
                this.isSaving = false;
            }
        }
    },

    mounted() {
        this.loadData();
        window.addEventListener('pageshow', this.handlePageShow);
    },

    beforeDestroy() {
        window.removeEventListener('pageshow', this.handlePageShow);
    }
};



// WEBPACK FOOTER //
// ./src/components/user/dashboard/pages/clubs/view-club/view-club.js